import { ReactElement } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';

import { useStoreState, useStoreActions } from '~/store/hooks';
import { formatClientDefaultValues } from '~/utils/formatDefaultValues';
import { normalizeDateString, normalizeDateStringWithCurrentUtcTime } from '~/utils/date/date';
import { useApiMutation, useApiRequest } from '~/hooks/api';
import useClientMultiTeam from '~/hooks/useClientMultiTeamId';
import { IClinic } from '~/services/api/clinic/types';
import { IClientTeam } from '~/services/api/clientDetails/types';
import { CLIENT, VIEW_CLIENT } from '~/ui/constants/paths';
import ClientProfileTab from '~/ui/pages/Clients/LocalAdmin/ClientProfile/constants/clientProfileTabs';
import Breadcrumbs from '~/ui/components/common/Breadcrumbs';
import Loader from '~/ui/components/common/Loader';
import { IInitialValues } from '../Form/initialValues';
import { EDIT_CLIENT_LEAVE_DESCRIPTION } from '../Form/constants';
import Form from '../Form';

interface IParams {
  [key: string]: string;
}

type ILocationState = { state: { actTeamId: string } };

const EditClient = (): ReactElement => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { clientId } = useParams<IParams>();
  const location: ILocationState = useLocation();

  const clinic = useStoreState(state => state.clinic.current || ({} as IClinic));
  const client = useStoreState(state => state.client.current);

  const { getClientMultiTeam } = useClientMultiTeam();
  const clientTeam = getClientMultiTeam(client?.actTeams) as IClientTeam;

  const onGetMyClinic = useStoreActions(actions => actions.clinic.onGetMyClinic);
  const { onGetClient, onEditClient } = useStoreActions(actions => actions.client);

  const { name: clinicName, id: clinicId } = clinic;
  const teamId = location.state?.actTeamId;

  const { isLoading: isClinicLoading } = useApiRequest(() => onGetMyClinic(), {
    enabled: !clinicId,
  });
  const { isLoading: isClientLoading } = useApiRequest(
    () => onGetClient({ clinicId: String(clinicId), teamId, clientId }),
    { enabled: !!clinicId, initialLoading: true, dependencies: [clinicId, teamId, clientId] },
  );

  const editClient = ({ teamId: clientTeamId, emrMrn, dateOfBirth, ...rest }: IInitialValues) =>
    onEditClient({
      requestInfo: { clinicId: String(clinicId), teamId: String(clientTeamId), clientId },
      requestPayload: {
        dateOfBirth: normalizeDateString(dateOfBirth),
        emrMrn: emrMrn === '' ? null : emrMrn,
        ...rest,
        admissionDetails: {
          ...rest.admissionDetails,
          admissionDate: normalizeDateStringWithCurrentUtcTime(rest.admissionDetails.admissionDate),
        },
      },
    });

  const { mutate: onSubmit, isLoading: isEditing } = useApiMutation(editClient, {
    notifyMessage: 'Client successfully updated',
    onSuccess: () =>
      navigate(
        VIEW_CLIENT.replace(':clientId', String(client.id)).replace(
          ':actTeamId',
          String(clientTeam?.team.id),
        ),
        {
          state: {
            actTeamId: String(clientTeam?.team.id),
            selectedTab: ClientProfileTab.HealthDetails,
          },
        },
      ),
    throwOnError: false,
  });

  const itemsForBreadcrumbs = [
    { to: CLIENT, title: `${clinicName} Clients` },
    {
      to: {
        pathname: VIEW_CLIENT.replace(':clientId', String(client?.id)).replace(
          ':actTeamId',
          String(clientTeam?.team.id),
        ),
        state: { actTeamId: String(clientTeam?.team.id) },
      },
      title: 'Client Profile',
    },
    { to: pathname, title: 'Edit Client Profile' },
  ];

  if (isClinicLoading || isClientLoading || !clinicId || !client) return <Loader />;

  return (
    <div>
      <h2>Edit Client Profile</h2>
      {client && <Breadcrumbs itemsForBreadcrumbs={itemsForBreadcrumbs} />}
      <Form
        defaultValues={formatClientDefaultValues(client, clientTeam)}
        clinicId={String(clinicId)}
        leavePageDescription={EDIT_CLIENT_LEAVE_DESCRIPTION}
        isProcessing={isEditing}
        onSubmit={onSubmit}
        submitText="Save Changes"
      />
    </div>
  );
};

export default EditClient;
